import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import type { PicksFilterValue } from '@/utils/ballotPicks';

// Listed in `PERSISTENT_KEYS` in storageUtils, so it survives the `beforeunload`
// wipe on refresh but is cleared by `clearVoteMateStorageOnLogout` on sign-out.
const STORAGE_KEY = 'votemate_contest_filters';

type ContestFilterMap = Record<string, PicksFilterValue>;

const readFromStorage = (): ContestFilterMap => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return {};
    }
    const result: ContestFilterMap = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (value === 'picks' || value === 'all') {
        result[key] = value;
      }
    }
    return result;
  } catch {
    return {};
  }
};

/**
 * Per-contest filter state (`'picks' | 'all'`), set from a race's own toggle.
 * A race with no entry follows its election's master switch (see
 * `contestFilterValue`), and flipping the master clears that election's
 * entries. Drop-in replacement for
 * `useState<ContestFilterMap>({})` that persists across refresh under the
 * `votemate_contest_filters` key — paired with `useLikedCandidates` so a user's
 * "Your Picks" view is restored automatically when they return.
 */
const useContestFilters = (): [ContestFilterMap, Dispatch<SetStateAction<ContestFilterMap>>] => {
  const [filters, setFilters] = useState<ContestFilterMap>(readFromStorage);
  // Skip the no-op write on first render — the state was just hydrated from
  // localStorage, so persisting it back is pointless and writes `{}` every page
  // load for users who've never used the feature.
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    } catch {
      // localStorage may be full / disabled (private mode, quota) — fail silently.
    }
  }, [filters]);

  return [filters, setFilters];
};

export default useContestFilters;
